import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  RadioGroup,
  RadioGroupItem,
} from '@vnetwork/ui/primitives';
import { PasswordInput } from '@vnetwork/ui/shells';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useCreateStorage,
  useUpdateStorage,
  type Storage,
  type StorageInput,
} from '@/api/fastchannel';
import { trackFeatureAction } from '@/lib/analytics-events';

interface StorageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceUuid: string;
  /** Present → edit mode; absent → create mode. */
  storage?: Storage;
}

const TYPES = [
  { value: 's3', label: 'Amazon S3' },
  { value: 'minio', label: 'MinIO' },
];

export function StorageDialog({ open, onOpenChange, serviceUuid, storage }: StorageDialogProps) {
  const { show } = useToast();
  const isEdit = !!storage;

  const [name, setName] = useState(storage?.name ?? '');
  const [title, setTitle] = useState(storage?.title ?? '');
  const [type, setType] = useState(storage?.type ?? 's3');
  const [publicUrl, setPublicUrl] = useState(storage?.public_url ?? '');
  // Credential fields are write-only — never returned by the API. On edit they
  // start blank and are only sent when the user re-enters them.
  const [bucket, setBucket] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [secret, setSecret] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [region, setRegion] = useState('us-east-1');

  const create = useCreateStorage(serviceUuid, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Storage created' });
      trackFeatureAction('fastchannel', 'storage', 'create');
      onOpenChange(false);
    },
    onError: () => show({ tone: 'danger', title: 'Could not create storage' }),
  });
  const update = useUpdateStorage(serviceUuid, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Storage updated' });
      trackFeatureAction('fastchannel', 'storage', 'edit');
      onOpenChange(false);
    },
    onError: () => show({ tone: 'danger', title: 'Could not update storage' }),
  });

  const hasNewCredential = !!bucket || !!accessKey || !!secret;
  // Create: name + full credential required. Edit: name required; credential optional.
  const valid =
    name.trim().length > 0 &&
    (isEdit ? true : !!bucket && !!accessKey && !!secret);
  const busy = create.isPending || update.isPending;

  const submit = () => {
    const base: StorageInput = {
      name: name.trim(),
      title: title.trim(),
      type,
      public_url: publicUrl.trim(),
    };
    // Only include credential fields when supplied (so an edit that leaves them
    // blank keeps the stored credential).
    const withCred: StorageInput = hasNewCredential
      ? { ...base, bucket, access_key: accessKey, secret, endpoint: endpoint.trim(), region: region.trim() }
      : base;

    if (isEdit && storage) {
      update.mutate({ storageId: storage.id, input: withCred });
    } else {
      create.mutate(withCred);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit storage' : 'Add storage'}</DialogTitle>
          <DialogDescription>
            An S3/MinIO bucket the packager reads media from. Credentials are encrypted
            and never shown again.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Field label="Name" required description="Unique key for this storage.">
              {(props) => (
                <Input {...props} value={name} placeholder="vtv-s3" onChange={(e) => setName(e.target.value)} />
              )}
            </Field>
            <Field label="Title">
              {(props) => (
                <Input {...props} value={title} placeholder="VTV media storage" onChange={(e) => setTitle(e.target.value)} />
              )}
            </Field>
            <Field label="Type">
              {() => (
                <RadioGroup
                  value={type}
                  onValueChange={setType}
                  style={{ display: 'flex', gap: 'var(--vn-space-4)' }}
                >
                  {TYPES.map((t) => (
                    <label key={t.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <RadioGroupItem value={t.value} /> {t.label}
                    </label>
                  ))}
                </RadioGroup>
              )}
            </Field>
            <Field label="Public URL" description="Base URL used to build playable source links.">
              {(props) => (
                <Input
                  {...props}
                  value={publicUrl}
                  placeholder="https://cdn.example.com"
                  onChange={(e) => setPublicUrl(e.target.value)}
                />
              )}
            </Field>

            <div
              style={{
                borderTop: '1px solid var(--vn-border-default)',
                paddingTop: 'var(--vn-space-3)',
                display: 'grid',
                gap: 'var(--vn-space-4)',
              }}
            >
              <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                {isEdit
                  ? 'Credentials — leave blank to keep the current ones.'
                  : 'Credentials — stored encrypted, never displayed after saving.'}
              </span>
              <Field label="Bucket" required={!isEdit}>
                {(props) => (
                  <Input {...props} value={bucket} placeholder="my-bucket" onChange={(e) => setBucket(e.target.value)} />
                )}
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
                <Field label="Access key" required={!isEdit}>
                  {(props) => (
                    <Input {...props} value={accessKey} onChange={(e) => setAccessKey(e.target.value)} />
                  )}
                </Field>
                <Field label="Secret key" required={!isEdit}>
                  {(props) => (
                    <PasswordInput {...props} value={secret} onChange={(e) => setSecret(e.target.value)} />
                  )}
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--vn-space-3)' }}>
                <Field label="Endpoint" description="Blank for AWS S3.">
                  {(props) => (
                    <Input
                      {...props}
                      value={endpoint}
                      placeholder="https://s3.vnetwork.dev"
                      onChange={(e) => setEndpoint(e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Region">
                  {(props) => (
                    <Input {...props} value={region} onChange={(e) => setRegion(e.target.value)} />
                  )}
                </Field>
              </div>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!valid || busy} onClick={submit}>
            {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Add storage'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
